// @vendors
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon
} from "@heroicons/react/24/outline";

// @store
import { useCompanyStore } from '../../../store/useCompanyStore';
import { useSpinnerStore } from '../../../store/useSpinnerStore';
import useAuthStore from '../../../store/useAuthStore';

// @types
import { IFormDataCompany } from '../../../types/professional.types';
import { UserRole } from '../../../types/auth.types';

// @helpers
import { updateDataCompany } from '../../../helpers/functionsUtils';

const MyAccount = () => {
    const { t } = useTranslation();
    const { roleList } = useAuthStore();
    const { data, userData, getUser } = useCompanyStore();
    const { openSpinner } = useSpinnerStore();
    const [passwordVisible, setPasswordVisible] = useState(false);

    const renderName = () => {
        if (data?.name) return `${data?.name}`;
        return `${userData?.names}`
    }

    const renderLastName = () => {
        if (data?.lastNames) return data?.lastNames;
        return userData?.lastNames ? userData.lastNames : '';
    }

    const [formValue, setFormValue] = useState<IFormDataCompany>({
        name: renderName(),
        lastName: renderLastName(),
        phone: data?.phone ?? userData?.phoneNumber,
        desiredName: data?.desiredName ?? userData?.desiredName,
        emergencyContactName: data?.emergencyContactName ?? userData?.emergencyContactName,
        email: data?.email ?? userData?.email,
        password: "xxxxxxxxxx",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValue((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    const updateData = async (field: keyof IFormDataCompany) => {
        const params = {
            field: field,
            formValue,
            openSpinner,
            t,
            getProfessional: getUser,
            userType: UserRole.USER,
            userId: userData?.customerId ?? ''
        };
        return updateDataCompany(params);
    }

    const isProfessionalUser = () => {
        if (roleList?.includes(UserRole.PROFESSIONAL)) return true
        if (roleList?.includes(UserRole.USER)) return false
    }

    return (
        <div className='w-full h-[80vh] flex flex-col items-center p-6 gap-[50px]'>
            <h4 className='w-full text-[#1A1A1A] dark:text-white text-[15px] flex justify-center font-medium'>
                {t("userProfile.myAccount.title")}
            </h4>

            <section className='w-full grid grid-cols-1 xl:grid-cols-2 gap-[50px]'>
                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cTextSecondary">
                        {t("professional.profile.legalName")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <UserIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='name'
                            value={formValue.name}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b bg-white dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={isProfessionalUser()}
                        />
                        {!isProfessionalUser() &&
                            <button
                                className="text-sm text-[#F97068]"
                                onClick={() => updateData('name')}
                            >
                                {t("professional.profile.buttonEdit")}
                            </button>
                        }
                    </div>
                </div>

                {renderLastName() &&
                    <div>
                        <label className="text-sm font-medium text-[#262422] dark:text-dark-cTextSecondary">
                            {t("professional.profile.lastName")}
                        </label>
                        <div className="flex flex-row gap-1">
                            <UserIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                            <input
                                type="text"
                                name='lastName'
                                value={formValue.lastName}
                                onChange={(e) => handleChange(e)}
                                className="w-full py-2 border-b bg-white dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                                disabled={isProfessionalUser()}
                            />
                            {!isProfessionalUser() &&
                                <button
                                    className="text-sm text-[#F97068]"
                                    onClick={() => updateData('lastName')}
                                >
                                    {t("professional.profile.buttonEdit")}
                                </button>
                            }
                        </div>
                    </div>
                }

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cTextSecondary">
                        {t("professional.profile.phone")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <PhoneIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='phone'
                            value={formValue.phone}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b bg-white dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={isProfessionalUser()}
                        />
                        {!isProfessionalUser() &&
                            <button
                                className="text-sm text-[#F97068]"
                                onClick={() => updateData('phone')}
                            >
                                {t("professional.profile.buttonEdit")}
                            </button>
                        }
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cTextSecondary">
                        {t("professional.profile.desiredName")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <UserIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='desiredName'
                            value={formValue.desiredName}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b bg-white dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={isProfessionalUser()}
                        />
                        {!isProfessionalUser() &&
                            <button
                                className="text-sm text-[#F97068]"
                                onClick={() => updateData('desiredName')}
                            >
                                {t("professional.profile.buttonEdit")}
                            </button>
                        }
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cTextSecondary">
                        {t("professional.profile.emergencyContact")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <UserIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='emergencyContactName'
                            value={formValue.emergencyContactName}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b bg-white dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={isProfessionalUser()}
                        />
                        {!isProfessionalUser() &&
                            <button
                                className="text-sm text-[#F97068]"
                                onClick={() => updateData('emergencyContactName')}
                            >
                                {t("professional.profile.buttonEdit")}
                            </button>
                        }
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cTextSecondary">
                        {t("professional.profile.email")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <EnvelopeIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='email'
                            value={formValue.email}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b bg-white dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={true}
                        />

                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cTextSecondary">
                        {t("professional.profile.password")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <LockClosedIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type={passwordVisible ? "text" : "password"}
                            name='password'
                            value={formValue.password}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b bg-white dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={true}
                        />
                        {passwordVisible
                            ? <EyeSlashIcon className='w-[25px] mr-[5px] text-[#ABABAB]' onClick={() => setPasswordVisible(!passwordVisible)} />
                            : <EyeIcon className='w-[25px] mr-[5px] text-[#ABABAB]' onClick={() => setPasswordVisible(!passwordVisible)} />
                        }
                        {!isProfessionalUser() &&
                            <button
                                className="text-sm text-[#F97068]"
                            //onClick={() => updateData('email')}
                            >
                                {t("professional.profile.chengePass")}
                            </button>
                        }
                    </div>
                </div>
            </section>
        </div>
    )
}

export default MyAccount